<template>
  <FormGroup :error-container="errorContainer" :label="label" :name="name" :floating="floating">
    <div class="fm-country-select" :class="{ 'fm-country-select--open': open }" ref="root">

      <!--
        Hidden native <select> — never visible, but FormGroup's querySelectorAll picks it
        up for fm-has-value floating-label detection and the change event keeps it in sync.
      -->
      <select ref="native" class="fm-select__native" :name="name" :disabled="disabled"
              tabindex="-1" aria-hidden="true">
        <option value=""/>
        <option v-for="(cname, code) in Countries" :key="code" :value="code">{{ cname }}</option>
      </select>

      <!-- Visible trigger — shares .form-control for floating-label padding -->
      <div
        class="fm-select__trigger form-control"
        :class="{ 'fm-select__trigger--empty': !hasValue }"
        :tabindex="disabled ? -1 : 0"
        role="combobox"
        :aria-expanded="open"
        @click="!disabled && toggle()"
        @keydown.enter.prevent="!disabled && toggle()"
        @keydown.space.prevent="!disabled && toggle()"
        @keydown.escape="close"
        @keydown.tab="close"
      >
        <span class="fm-select__sel-icon" v-html="GLOBE_ICON" aria-hidden="true"/>
        <span class="fm-select__sel-text">{{ currentLabel }}</span>
        <svg class="fm-select__chevron" :class="{ 'fm-select__chevron--up': open }"
             width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">
          <path d="M1 1l4.5 5 4.5-5" stroke="currentColor" stroke-width="1.6"
                stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <!-- Dropdown panel — position:fixed so it escapes fm-card overflow:hidden -->
      <transition name="fm-drop">
        <div v-if="open" class="fm-country-select__menu" :style="menuStyle" role="listbox">

          <!-- Sticky search bar -->
          <div class="fm-country-search">
            <svg class="fm-country-search__icon" xmlns="http://www.w3.org/2000/svg"
                 width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
            </svg>
            <input
              ref="searchInput"
              v-model="query"
              type="text"
              class="fm-country-search__input"
              placeholder="Search countries…"
              autocomplete="off"
              @keydown.escape.prevent="close"
            />
            <button v-if="query" type="button" tabindex="-1"
                    class="fm-country-search__clear" @click.stop="query = ''" aria-label="Clear search">
              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>

          <!-- Scrollable country list -->
          <div class="fm-country-select__list" ref="list">
            <div
              v-for="[code, cname] in filteredCountries"
              :key="code"
              class="fm-select__item"
              :class="{ 'fm-select__item--active': isSelected(code) }"
              role="option"
              :aria-selected="isSelected(code)"
              @click="pick(code)"
            >
              <span class="fm-select__item-icon" v-html="GLOBE_ICON" aria-hidden="true"/>
              <span class="fm-select__item-label">{{ cname }}</span>
              <svg v-if="isSelected(code)" class="fm-select__item-check"
                   width="13" height="10" viewBox="0 0 13 10" fill="none" aria-hidden="true">
                <path d="M1.5 5L5 8.5L11.5 1.5" stroke="#FFB000" stroke-width="2"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div v-if="!filteredCountries.length" class="fm-country-select__empty">
              No countries match <em>"{{ query }}"</em>
            </div>
          </div>

        </div>
      </transition>

    </div>
  </FormGroup>
</template>

<script>
import FormGroup from '@modules/shared/components/form/FormGroup.vue';
import Countries from '@modules/shared/constants/countries.js';

const GLOBE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 16 16"><path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855-.19.353-.36.744-.5 1.168h2.387V1.077zM4.053 4a9.336 9.336 0 0 1 .64-1.539A6.644 6.644 0 0 1 5.29 1.06 7.008 7.008 0 0 0 2.255 4h1.798zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a6.958 6.958 0 0 0-.656 2.5h2.453zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5H4.847zM8.5 5v2.5h2.99a12.495 12.495 0 0 0-.337-2.5H8.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5H4.51zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5H8.5zm2.145 3.5H8.5v2.923c.67-.204 1.335-.82 1.887-1.855.19-.353.36-.744.5-1.168H10.645zm.872-1h1.798a7.008 7.008 0 0 0-3.035-2.941 6.644 6.644 0 0 1 .597.933A9.336 9.336 0 0 1 11.517 11zM11.18 5h-1.798a7.008 7.008 0 0 1 3.035 2.941 6.644 6.644 0 0 0-.597-.933A9.336 9.336 0 0 0 11.18 5z"/></svg>`;

export default {
  name: 'CountrySelect',
  components: { FormGroup },
  model: { prop: 'value', event: 'input' },
  props: {
    name:           { type: String,  default: 'country' },
    label:          { type: String,  default: 'Passport Country' },
    value:          { type: String,  default: '' },
    disabled:       { default: false },
    errorContainer: { type: String,  default: '' },
    allowEmpty:     { type: Boolean, default: false },
    floating:       { type: Boolean, default: false },
    /**
     * What `value`/`input` carry:
     *   'code' (default) — ISO alpha-3 code, e.g. "AUS". Used by passport
     *                       country, which stores/validates the code.
     *   'name'            — the English name, e.g. "Australia". Used by the
     *                       address current/previous country fields, which
     *                       store/validate the name (config('country_code')
     *                       on the backend), not the code.
     */
    valueBy:        { type: String,  default: 'code', validator: (v) => ['code', 'name'].includes(v) },
  },
  data() {
    return {
      open: false,
      query: '',
      menuStyle: {},
      Countries,
      GLOBE_ICON,
    };
  },
  computed: {
    proxyValue: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    hasValue() {
      return this.proxyValue !== null && this.proxyValue !== undefined && this.proxyValue !== '';
    },
    currentLabel() {
      if (!this.hasValue) return '';
      if (this.valueBy === 'name') return this.proxyValue;
      return Countries[this.proxyValue] || this.proxyValue;
    },
    filteredCountries() {
      const q = this.query.trim().toLowerCase();
      const entries = Object.entries(Countries);
      if (!q) return entries;
      return entries.filter(([, name]) => name.toLowerCase().includes(q));
    },
  },
  methods: {
    _calcMenuStyle() {
      const trigger = this.$el.querySelector('.fm-select__trigger');
      if (!trigger) return;
      const r = trigger.getBoundingClientRect();
      this.menuStyle = {
        position: 'fixed',
        top:   `${r.bottom + 6}px`,
        left:  `${r.left}px`,
        width: `${r.width}px`,
      };
    },
    toggle() {
      if (this.open) {
        this.close();
      } else {
        this._calcMenuStyle();
        this.open = true;
        this.query = '';
        this.$nextTick(() => {
          this.$refs.searchInput?.focus();
          // Scroll the currently selected item into view
          const active = this.$refs.list?.querySelector('.fm-select__item--active');
          if (active) active.scrollIntoView({ block: 'nearest' });
        });
      }
    },
    close() {
      this.open = false;
      this.query = '';
    },
    pick(code) {
      this.proxyValue = this.valueBy === 'name' ? (Countries[code] ?? code) : code;
      this.close();
      // Sync hidden select so FormGroup keeps fm-has-value up to date
      this.$nextTick(() => {
        if (this.$refs.native) {
          this.$refs.native.value = code;
          this.$refs.native.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    },
    isSelected(code) {
      return code === this.codeFor(this.proxyValue);
    },
    // The hidden native <select> and the visible menu are always keyed by
    // code — this converts a name-mode proxyValue back to its code so both
    // can still compare/sync against it.
    codeFor(val) {
      if (this.valueBy !== 'name') return val;
      return Object.keys(Countries).find((code) => Countries[code] === val) ?? '';
    },
    syncNative(val) {
      if (this.$refs.native) {
        this.$refs.native.value = this.codeFor(val) || '';
      }
    },
  },
  watch: {
    // Keep hidden select in sync when value is set externally (draft restore, etc.)
    value(val) { this.syncNative(val); },
    // Reset list scroll position whenever the search query changes
    query() {
      this.$nextTick(() => {
        if (this.$refs.list) this.$refs.list.scrollTop = 0;
      });
    },
  },
  mounted() {
    this.syncNative(this.proxyValue);
    // Close when clicking outside the component
    this._onOutside = (e) => { if (!this.$el.contains(e.target)) this.close(); };
    document.addEventListener('click', this._onOutside, true);
    // Recompute fixed position while open so scrolling doesn't misalign the menu
    this._onScroll = () => { if (this.open) this._calcMenuStyle(); };
    window.addEventListener('scroll', this._onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this._onScroll, { passive: true });
  },
  beforeDestroy() {
    document.removeEventListener('click', this._onOutside, true);
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._onScroll);
  },
};
</script>

<style>
/* ── Country Select wrapper ──────────────────────────────────────────────── */
.fm-country-select {
  position: relative;
}

/* Amber focus ring on the trigger when dropdown is open */
.fm-country-select--open .fm-select__trigger {
  border-color: #FFB000;
  box-shadow: 0 0 0 3px rgba(255, 176, 0, 0.12);
  outline: none;
}

/* ── Dropdown panel ──────────────────────────────────────────────────────── */
/* position/top/left/width are set via inline :style (fixed, from getBoundingClientRect)
   so the menu escapes any ancestor overflow:hidden (e.g. .fm-card). */
.fm-country-select__menu {
  background: #fff;
  border: 1.5px solid #E7E4DA;
  border-radius: 14px;
  box-shadow: 0 8px 28px rgba(21, 23, 28, 0.13), 0 2px 8px rgba(21, 23, 28, 0.06);
  z-index: 1060;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Search bar ──────────────────────────────────────────────────────────── */
.fm-country-search {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-bottom: 1.5px solid #F0EEE7;
  background: #FBFAF6;
  flex-shrink: 0;
}

.fm-country-search__icon {
  display: inline-flex;
  align-items: center;
  color: #9197A1;
  flex-shrink: 0;
}

.fm-country-search__input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 13.5px;
  color: #15171C;
  min-width: 0;
}

.fm-country-search__input::placeholder {
  color: #9197A1;
}

.fm-country-search__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: #9197A1;
  border-radius: 4px;
  line-height: 1;
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}

.fm-country-search__clear:hover {
  background: rgba(21, 23, 28, 0.07);
  color: #5B606B;
}

/* ── Scrollable list ─────────────────────────────────────────────────────── */
.fm-country-select__list {
  overflow-y: auto;
  max-height: 280px;
  scrollbar-width: thin;
  scrollbar-color: #E7E4DA transparent;
}

.fm-country-select__list::-webkit-scrollbar       { width: 4px; }
.fm-country-select__list::-webkit-scrollbar-track { background: transparent; }
.fm-country-select__list::-webkit-scrollbar-thumb { background: #E0DDD5; border-radius: 4px; }

/* Empty state */
.fm-country-select__empty {
  padding: 20px 16px;
  text-align: center;
  font-size: 13.5px;
  color: #9197A1;
}
</style>
